import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../context/StoreContext';
import { ProductCard } from '../entities/product/ProductCard';
import { categoryName, formatPrice, getProductPrice } from '../entities/product/helpers';
import { Icon } from '../shared/ui/Icon';

export function CatalogPage() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [priceMin, setPriceMin] = useState(null);
  const [priceMax, setPriceMax] = useState(null);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [queryReady, setQueryReady] = useState(false);
  const {
    category, dealsOnly, favoritesOnly, search, sort, products, categories, displayedProducts, favorites,
    resourceStatus, resourceErrors, retryCoreData,
    go, browseDeals, setCategory, setDealsOnly, setFavoritesOnly, setSearch, setSort,
  } = useStore();

  const brands = useMemo(() => [...new Set(products.map((item) => item.brand).filter(Boolean))].sort(), [products]);
  const priceBounds = useMemo(() => {
    const prices = products.map(getProductPrice).filter((price) => price > 0);
    return prices.length
      ? { min: Math.floor(Math.min(...prices)), max: Math.ceil(Math.max(...prices)) }
      : { min: 0, max: 0 };
  }, [products]);
  const activePriceMin = priceMin ?? priceBounds.min;
  const activePriceMax = priceMax ?? priceBounds.max;
  const filteredProducts = useMemo(() => displayedProducts.filter((product) => {
    const price = getProductPrice(product);
    const matchingPrice = price >= activePriceMin && price <= activePriceMax;
    const matchingBrand = !selectedBrands.length || selectedBrands.includes(product.brand);
    return matchingPrice && matchingBrand;
  }), [displayedProducts, activePriceMin, activePriceMax, selectedBrands]);
  const brandCounts = useMemo(() => Object.fromEntries(
    brands.map((brandName) => [brandName, displayedProducts.filter((product) => (
      product.brand === brandName && getProductPrice(product) >= activePriceMin && getProductPrice(product) <= activePriceMax
    )).length]),
  ), [brands, displayedProducts, activePriceMin, activePriceMax]);
  const categoryCounts = useMemo(() => Object.fromEntries(
    categories.map((item) => [item.slug, products.filter((product) => (
      String(product.category || '').toLowerCase() === String(item.slug || '').toLowerCase()
      && (!dealsOnly || (Number(product.salePrice) > 0 && Number(product.salePrice) < Number(product.price)))
      && (!favoritesOnly || favorites.includes(String(product.id)))
      && (!search || `${product.title || product.name || ''} ${product.brand || ''} ${product.category || ''} ${product.description || ''}`.toLowerCase().includes(search.toLowerCase()))
      && (!selectedBrands.length || selectedBrands.includes(product.brand))
      && getProductPrice(product) >= activePriceMin
      && getProductPrice(product) <= activePriceMax
    )).length]),
  ), [categories, products, dealsOnly, favoritesOnly, favorites, search, selectedBrands, activePriceMin, activePriceMax]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const min = Number(params.get('min'));
    const max = Number(params.get('max'));
    if (params.has('category')) setCategory(params.get('category') || '');
    if (params.has('deals')) setDealsOnly(params.get('deals') === '1');
    if (params.has('favorites')) setFavoritesOnly(params.get('favorites') === '1');
    if (params.has('search')) setSearch(params.get('search') || '');
    if (params.has('sort')) setSort(params.get('sort') || 'newest');
    if (Number.isFinite(min) && params.has('min')) setPriceMin(min);
    if (Number.isFinite(max) && params.has('max')) setPriceMax(max);
    if (params.has('brand')) setSelectedBrands(params.get('brand').split(',').filter(Boolean));
    setQueryReady(true);
  // Initialize filters from the URL only when the catalog mounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!queryReady) return;
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (dealsOnly) params.set('deals', '1');
    if (favoritesOnly) params.set('favorites', '1');
    if (search) params.set('search', search);
    if (sort !== 'newest') params.set('sort', sort);
    if (priceMin !== null) params.set('min', String(priceMin));
    if (priceMax !== null) params.set('max', String(priceMax));
    if (selectedBrands.length) params.set('brand', selectedBrands.join(','));
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [queryReady, category, dealsOnly, favoritesOnly, search, sort, priceMin, priceMax, selectedBrands]);

  const toggleBrand = (brandName) => {
    setSelectedBrands((current) => current.includes(brandName)
      ? current.filter((item) => item !== brandName)
      : [...current, brandName]);
  };

  const clearFilters = () => {
    setCategory('');
    setDealsOnly(false);
    setFavoritesOnly(false);
    setSearch('');
    setPriceMin(null);
    setPriceMax(null);
    setSelectedBrands([]);
    setFiltersOpen(false);
  };

  const setPriceBound = (bound, value) => {
    const next = Math.max(priceBounds.min, Math.min(priceBounds.max, Number(value)));
    if (bound === 'min') setPriceMin(Math.min(next, activePriceMax));
    else setPriceMax(Math.max(next, activePriceMin));
  };

  const removeChip = (type, value) => {
    if (type === 'brand') setSelectedBrands((current) => current.filter((brand) => brand !== value));
    if (type === 'category') setCategory('');
    if (type === 'deals') setDealsOnly(false);
    if (type === 'favorites') setFavoritesOnly(false);
    if (type === 'search') setSearch('');
    if (type === 'price') { setPriceMin(null); setPriceMax(null); }
  };

  const activeChips = [
    ...(category ? [{ type: 'category', value: categoryName(categories.find((item) => item.slug === category) || category) }] : []),
    ...(dealsOnly ? [{ type: 'deals', value: 'Deals' }] : []),
    ...(favoritesOnly ? [{ type: 'favorites', value: 'Favorites' }] : []),
    ...(search ? [{ type: 'search', value: `Search: ${search}` }] : []),
    ...selectedBrands.map((brand) => ({ type: 'brand', value: brand })),
    ...(priceMin !== null || priceMax !== null ? [{ type: 'price', value: `${formatPrice(priceMin ?? priceBounds.min)}–${formatPrice(priceMax ?? priceBounds.max)}` }] : []),
  ];

  if (resourceStatus.products === 'error') {
    return (
      <section className="resource-error" role="alert">
        <h2>We can’t load products right now.</h2>
        <p>{resourceErrors.products}</p>
        <button className="button button-primary" type="button" onClick={retryCoreData}>Try again</button>
      </section>
    );
  }

  if (resourceStatus.products === 'loading') {
    return (
      <div className="catalog-loading" aria-label="Loading products">
        <div className="catalog-skeleton-heading"><i /><i /><i /></div>
        <div className="product-grid">
          {Array.from({ length: 8 }, (_, index) => <div className="product-card-skeleton" key={index} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="catalog-page">
      <div className="breadcrumb">
        <button onClick={() => go('home')}>Home</button>
        <i>/</i>
        <span>
          {dealsOnly ? 'Deals & Discounts' : favoritesOnly ? 'Favorites' : category ? categoryName(categories.find((item) => item.slug === category) || category) : search ? `Search: "${search}"` : 'All Products'}
        </span>
      </div>

      <div className="catalog-title">
        <div>
          <p className="eyebrow">THE TECHNOMAX COLLECTION</p>
          <h1>
            {dealsOnly
              ? 'Special Offers & Discounts'
              : favoritesOnly
                ? 'Your Favorites'
              : category
                ? categoryName(categories.find((item) => item.slug === category) || category)
                : search
                  ? `Results for "${search}"`
                  : 'Find Your Next Tech Essential.'}
          </h1>
          <p>{displayedProducts.length} products available</p>
        </div>
        <div className="catalog-controls">
          <label className="sort-select">
            Sort by{' '}
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Featured & Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="rating">Top Customer Rated</option>
            </select>
          </label>
        </div>
      </div>

      <div className="catalog-layout">
        {filtersOpen && (
          <button
            type="button"
            className="filter-drawer-backdrop"
            aria-label="Close filters"
            onClick={() => setFiltersOpen(false)}
          />
        )}
        <aside className={`catalog-filters ${filtersOpen ? 'is-open' : ''}`} aria-label="Product filters">
          <button type="button" className="filter-close" onClick={() => setFiltersOpen(false)}>
            <span>Filters</span><Icon>×</Icon>
          </button>
          <p className="eyebrow">FILTER BY CATEGORY</p>
          {resourceStatus.categories === 'loading' && <span className="category-filter-skeleton" aria-hidden="true" />}
          {resourceStatus.categories === 'error' && (
            <div className="catalog-filter-error">
              <span>{resourceErrors.categories}</span>
              <button type="button" onClick={() => retryCoreData()}>Retry</button>
            </div>
          )}
          <button
            className={!category && !dealsOnly ? 'selected' : ''}
            onClick={() => { setCategory(''); setDealsOnly(false); setFavoritesOnly(false); setFiltersOpen(false); }}
          >
            All products <span>{products.length}</span>
          </button>
          <button className={dealsOnly ? 'selected' : ''} onClick={() => { browseDeals(); setFiltersOpen(false); }}>
            Deals & Sales             <span>{products.filter((item) => Number(item.salePrice) > 0 && Number(item.salePrice) < Number(item.price)).length}</span>
          </button>
          {categories.map((item) => {
            const count = categoryCounts[item.slug] || 0;
            return (
            <button
              className={category === item.slug && !dealsOnly ? 'selected' : ''}
              key={item.slug}
              disabled={count === 0 && category !== item.slug}
              onClick={() => { setCategory(item.slug); setDealsOnly(false); setFavoritesOnly(false); setFiltersOpen(false); }}
            >
              {categoryName(item)}
              <span>{count}</span>
            </button>
            );
          })}

          <div className="filter-rule" />
          <p className="eyebrow">PRICE RANGE</p>
          <div className="price-range-dual">
            <div className="price-range-track">
              <span style={{
                left: `${priceBounds.max > priceBounds.min ? ((activePriceMin - priceBounds.min) / (priceBounds.max - priceBounds.min)) * 100 : 0}%`,
                right: `${priceBounds.max > priceBounds.min ? 100 - ((activePriceMax - priceBounds.min) / (priceBounds.max - priceBounds.min)) * 100 : 0}%`,
              }} />
            </div>
            <input className="price-slider price-slider-min" type="range" aria-label="Minimum price" min={priceBounds.min} max={priceBounds.max} value={activePriceMin} disabled={priceBounds.max <= priceBounds.min} onChange={(event) => setPriceBound('min', event.target.value)} />
            <input className="price-slider price-slider-max" type="range" aria-label="Maximum price" min={priceBounds.min} max={priceBounds.max} value={activePriceMax} disabled={priceBounds.max <= priceBounds.min} onChange={(event) => setPriceBound('max', event.target.value)} />
          </div>
          <div className="price-inputs">
            <label>Min <input type="number" min={priceBounds.min} max={activePriceMax} value={activePriceMin} disabled={priceBounds.max <= priceBounds.min} onChange={(event) => setPriceBound('min', event.target.value)} /></label>
            <label>Max <input type="number" min={activePriceMin} max={priceBounds.max} value={activePriceMax} disabled={priceBounds.max <= priceBounds.min} onChange={(event) => setPriceBound('max', event.target.value)} /></label>
          </div>

          {brands.length > 0 && (
            <>
              <div className="filter-rule" />
              <p className="eyebrow">BRAND</p>
              {brands.map((brandName) => (
                <label className="brand-filter" key={brandName}>
                  <input type="checkbox" aria-label={`Filter by ${brandName}`} checked={selectedBrands.includes(brandName)} disabled={!brandCounts[brandName] && !selectedBrands.includes(brandName)} onChange={() => toggleBrand(brandName)} />
                  <span>{brandName}</span><small>{brandCounts[brandName]}</small>
                </label>
              ))}
            </>
          )}
        </aside>

        <div className="catalog-products">
          <div className="catalog-result-row">
            <p className="catalog-result-count">
              Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            </p>
            <button type="button" className="button button-outline filter-trigger" onClick={() => setFiltersOpen(true)}>
              <Icon>▦</Icon> Filters
            </button>
          </div>
          {activeChips.length > 0 && (
            <div className="active-filter-row" aria-label="Active filters">
              {activeChips.map((chip) => (
                <button key={`${chip.type}-${chip.value}`} type="button" className="active-filter-chip" onClick={() => removeChip(chip.type, chip.type === 'brand' ? chip.value : undefined)}>
                  {chip.value}<span aria-hidden="true">×</span>
                </button>
              ))}
              <button type="button" className="clear-filters-link" onClick={clearFilters}>Clear all</button>
            </div>
          )}
          {filteredProducts.length ? (
            <div className="product-grid">
              {filteredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <h2>{products.length ? 'No items match your selection.' : 'No products are available yet.'}</h2>
              <p>{products.length ? `No products match ${activeChips.map((chip) => chip.value).join(', ')}. Remove a filter or clear all to see more products.` : 'The store catalog is currently empty.'}</p>
              <button
                className="button button-primary"
                onClick={clearFilters}
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
